import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, TrendingUp, Megaphone, Sparkles, Monitor, Truck, Wallet } from 'lucide-react';
import { PillPrimary, PillOutline } from '../components/PillButton';
import { heroImages, pillars, collaborationLevels, services6, otherServices, processSteps, stats } from '../mock';
import { api, resolveImage } from '../lib/api';

const serviceIcons = [TrendingUp, Megaphone, Sparkles, Monitor, Truck, Wallet];

const TemporaryManagement = () => {
  const [form, setForm] = useState({ name: '', company: '', revenue: '', email: '', phone: '', desc: '' });
  const [sent, setSent] = useState(false);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    api.listCaseStudies().then((cs) => setCaseStudies(cs.slice(0, 3))).catch(() => {});
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.getElementById(window.location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.createLead({
        source: 'tm',
        name: form.name,
        company: form.company,
        revenue: form.revenue,
        email: form.email,
        phone: form.phone,
        message: form.desc,
      });
      setSent(true);
      setTimeout(() => setSent(false), 4000);
      setForm({ name: '', company: '', revenue: '', email: '', phone: '', desc: '' });
    } catch (err) {
      alert('Errore nell\u2019invio. Riprova.');
    }
  };

  return (
    <div className="bg-[#2b2b2b]">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${heroImages.team1})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#141414] via-[#2b2b2b]/90 to-[#2b2b2b]" />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-8">Nuovo dipartimento</div>
            <h1 className="font-serif text-white text-5xl md:text-7xl leading-[1.05] mb-8">ETech Temporary Management</h1>
            <p className="font-serif italic text-[#e9dfc0] text-xl md:text-2xl leading-relaxed mb-10 max-w-3xl">
              “Non vi diciamo cosa fare. Veniamo a farlo con voi — e in alcuni casi, ci mettiamo i soldi anche noi.”
            </p>
            <p className="text-[#e9dfc0]/85 text-[16px] md:text-[17px] leading-relaxed max-w-3xl mb-10">
              Oltre <span className="text-white font-semibold">50 brand lanciati</span>. Oltre <span className="text-white font-semibold">500k€ investiti in campagne pubblicitarie</span> negli ultimi 12 mesi, in modo profittevole e con risultati costanti nel tempo. Non chiediamo solo una tariffa mensile: in alcuni casi investiamo direttamente i nostri soldi, assumendoci lo stesso rischio dei nostri partner.
            </p>
            <div className="flex flex-wrap gap-3">
              <PillPrimary href="#cta">Prenota una call conoscitiva</PillPrimary>
              <PillOutline href="#livelli">I 3 livelli di collaborazione</PillOutline>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((s) => (
              <div key={s.label} className="bg-[#1e1e1e]/80 backdrop-blur border border-[#e9dfc0]/20 rounded-2xl p-8">
                <div className="font-serif text-[#e9dfc0] text-5xl md:text-6xl mb-3">{s.number}</div>
                <p className="text-[#e9dfc0]/80 text-sm leading-relaxed">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perché non siamo un'agenzia */}
      <section id="perche-agenzia" className="bg-[#3a3a3a] py-24 scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-6">Il nostro posizionamento</div>
            <h2 className="font-serif text-white text-4xl md:text-5xl leading-[1.1] mb-8">Perché non siamo un'agenzia</h2>
            <p className="text-[#e9dfc0]/90 text-[16px] leading-relaxed">
              Hai mai lavorato con un'agenzia di social marketing? “Questa è la nostra offerta, questa è la nostra fee mensile, questo è il nostro lavoro.” Pochissima flessibilità, ancora meno disponibilità e reperibilità. Abbiamo lavorato con decine di agenzie in Italia e all'estero, tutte con le stesse criticità: ti trattano da cliente, non da partner. Per questo abbiamo deciso di posizionarci come un'azienda di rottura, entrando in partnership diretta con i nostri clienti, investendo in prima persona nelle loro attività.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {pillars.map((p, i) => (
              <div key={p.title} className="bg-[#2b2b2b] border border-[#e9dfc0]/20 rounded-2xl p-8 hover:border-[#e9dfc0]/50 transition-colors">
                <div className="font-serif text-[#e9dfc0] text-5xl mb-4 opacity-40">0{i + 1}</div>
                <h3 className="font-serif text-white text-2xl mb-4">{p.title}</h3>
                <p className="text-[#e9dfc0]/85 text-[15px] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Livelli di collaborazione */}
      <section id="livelli" className="bg-[#2b2b2b] py-24 scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-6">Modelli di partnership</div>
            <h2 className="font-serif text-white text-4xl md:text-5xl leading-[1.1] mb-4">I nostri 3 livelli di collaborazione</h2>
            <p className="text-[#e9dfc0]/70 text-[15px]">Ogni brand ha esigenze diverse. In call conoscitiva individuiamo insieme il modello più adatto.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {collaborationLevels.map((l) => (
              <div key={l.id} className={`rounded-2xl p-8 border transition-colors ${l.featured ? 'bg-[#e9dfc0] border-[#e9dfc0]' : 'bg-[#1e1e1e] border-[#e9dfc0]/20 hover:border-[#e9dfc0]/50'}`}>
                <h3 className={`font-serif text-2xl mb-6 ${l.featured ? 'text-[#2b2b2b]' : 'text-white'}`}>{l.label}</h3>
                <p className={`text-sm mb-4 font-semibold ${l.featured ? 'text-[#2b2b2b]' : 'text-[#e9dfc0]'}`}>Per chi è</p>
                <p className={`text-[15px] mb-6 leading-relaxed ${l.featured ? 'text-[#2b2b2b]/85' : 'text-[#e9dfc0]/85'}`}>{l.forWho}</p>
                <p className={`text-sm mb-4 font-semibold ${l.featured ? 'text-[#2b2b2b]' : 'text-[#e9dfc0]'}`}>Cosa include</p>
                <p className={`text-[15px] leading-relaxed ${l.featured ? 'text-[#2b2b2b]/85' : 'text-[#e9dfc0]/85'}`}>{l.includes}</p>
              </div>
            ))}
          </div>
          <p className="font-serif italic text-[#e9dfc0]/90 text-center text-lg md:text-xl mt-12 max-w-2xl mx-auto">
            “Hai un'idea e non sai come svilupparla? Presentala a ETech e ti aiuteremo a farla diventare realtà, con un supporto step-by-step in ogni fase della crescita.”
          </p>
        </div>
      </section>

      {/* Cosa facciamo */}
      <section id="servizi" className="bg-[#1e1e1e] py-24 scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-6">Cosa facciamo</div>
            <h2 className="font-serif text-white text-4xl md:text-5xl leading-[1.1]">Un vero reparto interno, full-stack.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services6.map((s, i) => {
              const Icon = serviceIcons[i];
              return (
                <div key={s.title} className="bg-[#2b2b2b] border border-[#e9dfc0]/15 rounded-xl p-7 hover:border-[#e9dfc0]/45 hover:-translate-y-1 transition-all">
                  <div className="w-11 h-11 rounded-full bg-[#e9dfc0]/15 text-[#e9dfc0] flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-white text-xl mb-3">{s.title}</h3>
                  <p className="text-[#e9dfc0]/80 text-[14px] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10 bg-[#2b2b2b] border border-[#e9dfc0]/15 rounded-xl p-8">
            <h4 className="font-serif text-white text-xl mb-5">Altri servizi su richiesta</h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {otherServices.map((s) => (
                <li key={s} className="flex items-start gap-3 text-[#e9dfc0]/85 text-[14px]">
                  <Check className="w-4 h-4 text-[#e9dfc0] mt-1 flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Come lavoriamo */}
      <section id="come-lavoriamo" className="bg-[#3a3a3a] py-24 scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-6">Il processo</div>
            <h2 className="font-serif text-white text-4xl md:text-5xl leading-[1.1]">Come lavoriamo</h2>
          </div>
          <div className="space-y-4">
            {processSteps.map((s) => (
              <div key={s.n} className="bg-[#2b2b2b] border border-[#e9dfc0]/20 rounded-xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 hover:border-[#e9dfc0]/50 transition-colors">
                <div className="font-serif text-[#e9dfc0] text-4xl md:text-5xl min-w-[80px]">{s.n}</div>
                <div className="flex-1">
                  <h3 className="font-serif text-white text-xl md:text-2xl mb-2">{s.title}</h3>
                  <p className="text-[#e9dfc0]/85 text-[15px]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case study anteprima */}
      <section className="bg-[#2b2b2b] py-24">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="inline-block px-5 py-2.5 rounded-full border border-[#e9dfc0]/40 text-[#e9dfc0] text-[13px] mb-6">Prova sociale</div>
              <h2 className="font-serif text-white text-4xl md:text-5xl">Case study</h2>
            </div>
            <PillOutline to="/casi-studio">Vedi tutti i casi studio</PillOutline>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.slice(0, 3).map((cs) => (
              <Link key={cs.slug} to={`/casi-studio/${cs.slug}`} className="group bg-[#1e1e1e] border border-[#e9dfc0]/15 rounded-2xl overflow-hidden hover:border-[#e9dfc0]/40 transition-colors">
                <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: `url(${resolveImage(cs.image)})` }} />
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-serif text-[#e9dfc0] text-3xl">{cs.metric}</span>
                    <span className="text-[#e9dfc0]/60 text-sm">· {cs.time}</span>
                  </div>
                  <h3 className="text-white font-serif text-xl mb-2">{cs.title}</h3>
                  <p className="text-[#e9dfc0]/70 text-sm">{cs.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section id="cta" className="bg-[#e9dfc0] py-24 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif text-[#2b2b2b] text-4xl md:text-5xl mb-4 text-center">Parliamone.</h2>
          <p className="text-[#2b2b2b]/80 text-center mb-12 text-[16px]">30 minuti per capire se ETech Temporary Management è la mossa giusta per il tuo brand.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome" className="w-full bg-white border border-[#2b2b2b]/20 text-[#2b2b2b] placeholder:text-[#2b2b2b]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#2b2b2b]" />
              <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Azienda" className="w-full bg-white border border-[#2b2b2b]/20 text-[#2b2b2b] placeholder:text-[#2b2b2b]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#2b2b2b]" />
            </div>
            <select required value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} className="w-full bg-white border border-[#2b2b2b]/20 text-[#2b2b2b] rounded-lg py-4 px-5 focus:outline-none focus:border-[#2b2b2b]">
              <option value="" disabled>Fatturato mensile indicativo</option>
              <option>Sotto 10k€</option>
              <option>10k€ – 50k€</option>
              <option>50k€ – 200k€</option>
              <option>Oltre 200k€</option>
            </select>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-white border border-[#2b2b2b]/20 text-[#2b2b2b] placeholder:text-[#2b2b2b]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#2b2b2b]" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono" className="w-full bg-white border border-[#2b2b2b]/20 text-[#2b2b2b] placeholder:text-[#2b2b2b]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#2b2b2b]" />
            </div>
            <textarea rows={4} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Descrizione breve del progetto" className="w-full bg-white border border-[#2b2b2b]/20 text-[#2b2b2b] placeholder:text-[#2b2b2b]/50 rounded-lg py-4 px-5 focus:outline-none focus:border-[#2b2b2b] resize-none" />
            <div className="flex items-center gap-4">
              <button type="submit" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#2b2b2b] text-[#e9dfc0] text-[15px] font-medium hover:bg-[#1e1e1e] transition-colors">
                Prenota la call <ArrowRight className="w-4 h-4" />
              </button>
              {sent && <p className="text-[#2b2b2b] text-sm">Richiesta inviata! Ti contattiamo entro 24h.</p>}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default TemporaryManagement;
